import React, { useMemo, useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card, CardContent } from '../components/Card';
import { ExternalLink, RotateCw } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/Table';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/Dialog';
import { toast } from 'sonner';
import { Gavel, FileSearch, FileText, ShieldAlert, Hash, Clock, LinkIcon } from 'lucide-react';
import { RelatedWorkflow } from '../components/dashboard/RelatedWorkflow';
const reports = [{
  id: 'PRT-2026-0084',
  target: 'tiktok.com/@u**/v/abc***',
  platform: 'TikTok',
  category: 'Manipulated Media (Deepfake)',
  submitted: '2 jam lalu',
  status: 'UNDER REVIEW'
}, {
  id: 'PRT-2026-0083',
  target: 'x.com/presidenri_off***',
  platform: 'X',
  category: 'Impersonation',
  submitted: '4 jam lalu',
  status: 'REMOVED'
}, {
  id: 'PRT-2026-0082',
  target: 'facebook.com/p/12****',
  platform: 'Facebook',
  category: 'Misinformation',
  submitted: '6 jam lalu',
  status: 'REJECTED'
}, {
  id: 'PRT-2026-0081',
  target: 'youtube.com/watch?v=qz****',
  platform: 'YouTube',
  category: 'Spam / Bot Activity',
  submitted: '8 jam lalu',
  status: 'UNDER REVIEW'
}, {
  id: 'PRT-2026-0080',
  target: 't.me/papua_***',
  platform: 'Telegram',
  category: 'Doxxing / Threats',
  submitted: '12 jam lalu',
  status: 'ESCALATED'
}, {
  id: 'PRT-2026-0079',
  target: 'instagram.com/p/ab****',
  platform: 'Instagram',
  category: 'Coordinated Behavior',
  submitted: '1 hari lalu',
  status: 'REMOVED'
}, {
  id: 'PRT-2026-0078',
  target: 'x.com/buzzer_***',
  platform: 'X',
  category: 'Spam / Bot Activity',
  submitted: '1 hari lalu',
  status: 'REMOVED'
}, {
  id: 'PRT-2026-0077',
  target: 'tiktok.com/@dis***',
  platform: 'TikTok',
  category: 'Misinformation',
  submitted: '2 hari lalu',
  status: 'UNDER REVIEW'
}];
const statusColor: Record<string, string> = {
  'UNDER REVIEW': 'bg-amber-500/10 text-amber-500',
  REMOVED: 'bg-emerald-500/10 text-emerald-500',
  REJECTED: 'bg-red-500/10 text-red-500',
  ESCALATED: 'bg-orange-500/10 text-orange-500'
};
export function PlatformReporting() {
  const [platformFilter, setPlatformFilter] = useState<string>('SEMUA');
  const [statusFilter, setStatusFilter] = useState<string>('SEMUA');
  const [selected, setSelected] = useState<(typeof reports)[0] | null>(null);
  const platforms = ['SEMUA', 'X', 'TikTok', 'Facebook', 'Instagram', 'YouTube', 'Telegram'];
  const statuses = ['SEMUA', 'UNDER REVIEW', 'REMOVED', 'REJECTED', 'ESCALATED'];
  const filtered = useMemo(() => {
    return reports.filter(r => {
      if (platformFilter !== 'SEMUA' && r.platform !== platformFilter) return false;
      if (statusFilter !== 'SEMUA' && r.status !== statusFilter) return false;
      return true;
    });
  }, [platformFilter, statusFilter]);
  return <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6" data-id="element-2628">
      <PageHeader group="Hukum & Evidence" title="Platform Reporting Workflow" description="Pelaporan resmi konten ke platform (X, Meta, TikTok, YouTube, Telegram) sesuai prosedur trust & safety." icon={ExternalLink} data-id="element-2629" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2630">
        {[{
        l: 'Total Laporan 30D',
        v: '247',
        c: 'text-blue-500'
      }, {
        l: 'Berhasil Diturunkan',
        v: '142',
        c: 'text-emerald-500'
      }, {
        l: 'Dalam Review',
        v: '78',
        c: 'text-amber-500'
      }, {
        l: 'Ditolak',
        v: '27',
        c: 'text-red-500'
      }].map(k => <Card key={k.l} className="bg-card border-border/50" data-id="element-2631">
            <CardContent className="p-4" data-id="element-2632">
              <p className="font-mono text-[10px] uppercase text-muted-foreground" data-id="element-2633">
                {k.l}
              </p>
              <p className={`font-heading text-2xl font-bold mt-1 ${k.c}`} data-id="element-2634">
                {k.v}
              </p>
            </CardContent>
          </Card>)}
      </div>

      {/* Filter chips */}
      <div className="flex flex-col gap-3" data-id="element-2635">
        <div className="flex flex-wrap items-center gap-2" data-id="element-2636">
          <span className="font-mono text-[10px] uppercase text-muted-foreground mr-1" data-id="element-2637">
            Platform:
          </span>
          {platforms.map(p => <button key={p} onClick={() => setPlatformFilter(p)} className={`font-mono text-[10px] px-2 py-1 rounded border transition-colors ${platformFilter === p ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-muted/20 border-border/50 text-muted-foreground hover:bg-muted/30'}`} data-id="element-2638">
              {p}
            </button>)}
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-2639">
          <span className="font-mono text-[10px] uppercase text-muted-foreground mr-1" data-id="element-2640">
            Status:
          </span>
          {statuses.map(s => <button key={s} onClick={() => setStatusFilter(s)} className={`font-mono text-[10px] px-2 py-1 rounded border transition-colors ${statusFilter === s ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-muted/20 border-border/50 text-muted-foreground hover:bg-muted/30'}`} data-id="element-2641">
              {s}
            </button>)}
        </div>
      </div>

      <Card className="bg-card border-border/50" data-id="element-2642">
        <CardContent className="p-0" data-id="element-2643">
          <div className="overflow-x-auto" data-id="element-2644">
            <Table data-id="element-2645">
              <TableHeader className="bg-muted/20" data-id="element-2646">
                <TableRow className="border-border/50 hover:bg-transparent" data-id="element-2647">
                  <TableHead className="font-mono text-[9px]" data-id="element-2648">ID</TableHead>
                  <TableHead className="font-mono text-[9px]" data-id="element-2649">
                    TARGET URL
                  </TableHead>
                  <TableHead className="font-mono text-[9px]" data-id="element-2650">
                    PLATFORM
                  </TableHead>
                  <TableHead className="font-mono text-[9px]" data-id="element-2651">
                    KATEGORI
                  </TableHead>
                  <TableHead className="font-mono text-[9px]" data-id="element-2652">
                    DIKIRIM
                  </TableHead>
                  <TableHead className="font-mono text-[9px]" data-id="element-2653">STATUS</TableHead>
                  <TableHead className="font-mono text-[9px] text-right" data-id="element-2654">
                    AKSI
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-2655">
                {filtered.length === 0 ? <TableRow className="border-border/30 hover:bg-transparent" data-id="element-2656">
                    <TableCell colSpan={7} className="text-center py-12 text-xs text-muted-foreground font-mono" data-id="element-2657">
                      Tidak ada laporan yang cocok dengan filter saat ini.
                    </TableCell>
                  </TableRow> : filtered.map(r => <TableRow key={r.id} className="border-border/30 hover:bg-muted/10" data-id="element-2658">
                      <TableCell className="font-mono text-xs" data-id="element-2659">
                        {r.id}
                      </TableCell>
                      <TableCell className="font-mono text-[10px] text-muted-foreground truncate max-w-[200px]" data-id="element-2660">
                        {r.target}
                      </TableCell>
                      <TableCell data-id="element-2661">
                        <span className="font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded" data-id="element-2662">
                          {r.platform}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs" data-id="element-2663">{r.category}</TableCell>
                      <TableCell className="font-mono text-[10px] text-muted-foreground" data-id="element-2664">
                        {r.submitted}
                      </TableCell>
                      <TableCell data-id="element-2665">
                        <Badge variant="outline" className={`font-mono text-[8px] border-none ${statusColor[r.status]}`} data-id="element-2666">
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right" data-id="element-2667">
                        {r.status === 'REJECTED' ? <Button size="xs" variant="outline" className="h-6 text-[9px] font-mono" onClick={() => toast.success(`${r.id} dikirim ulang dengan bukti tambahan`)} data-id="element-2668">
                            <RotateCw className="size-3 mr-1" data-id="element-2669" />
                            Submit Ulang
                          </Button> : <Button size="xs" variant="outline" className="h-6 text-[9px] font-mono" onClick={() => setSelected(r)} data-id="element-2670">
                            Detail
                          </Button>}
                      </TableCell>
                    </TableRow>)}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={o => !o && setSelected(null)} data-id="element-2671">
        <DialogContent className="max-w-xl" data-id="element-2672">
          <DialogHeader data-id="element-2673">
            <div className="flex items-center gap-2 mb-1" data-id="element-2674">
              <span className="font-mono text-xs text-muted-foreground" data-id="element-2675">
                {selected?.id}
              </span>
              {selected && <Badge variant="outline" className={`font-mono text-[8px] border-none ${statusColor[selected.status]}`} data-id="element-2676">
                  {selected.status}
                </Badge>}
            </div>
            <DialogTitle data-id="element-2677">Detail Laporan Platform</DialogTitle>
            <DialogDescription data-id="element-2678">
              Pelaporan resmi ke platform sesuai prosedur trust & safety.
            </DialogDescription>
          </DialogHeader>
          {selected && <div className="space-y-3" data-id="element-2679">
              <div className="grid grid-cols-2 gap-3" data-id="element-2680">
                <div className="p-2.5 rounded bg-muted/20 border border-border/50" data-id="element-2681">
                  <p className="font-mono text-[10px] text-muted-foreground uppercase" data-id="element-2682">
                    Platform
                  </p>
                  <p className="text-sm font-bold mt-0.5" data-id="element-2683">
                    {selected.platform}
                  </p>
                </div>
                <div className="p-2.5 rounded bg-muted/20 border border-border/50" data-id="element-2684">
                  <p className="font-mono text-[10px] text-muted-foreground uppercase flex items-center gap-1" data-id="element-2685">
                    <Clock className="size-3" data-id="element-2686" /> Dikirim
                  </p>
                  <p className="text-sm font-bold mt-0.5" data-id="element-2687">
                    {selected.submitted}
                  </p>
                </div>
                <div className="p-2.5 rounded bg-muted/20 border border-border/50 col-span-2" data-id="element-2688">
                  <p className="font-mono text-[10px] text-muted-foreground uppercase" data-id="element-2689">
                    Kategori Pelanggaran
                  </p>
                  <p className="text-sm font-bold mt-0.5" data-id="element-2690">
                    {selected.category}
                  </p>
                </div>
                <div className="p-2.5 rounded bg-muted/20 border border-border/50 col-span-2" data-id="element-2691">
                  <p className="font-mono text-[10px] text-muted-foreground uppercase flex items-center gap-1" data-id="element-2692">
                    <LinkIcon className="size-3" data-id="element-2693" /> Target URL
                  </p>
                  <p className="font-mono text-xs break-all mt-0.5" data-id="element-2694">
                    {selected.target}
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-border/50 bg-muted/10 p-3" data-id="element-2695">
                <p className="font-mono text-[10px] uppercase text-muted-foreground mb-2 flex items-center gap-1" data-id="element-2696">
                  <Hash className="size-3" data-id="element-2697" /> Bukti & Chain of Custody
                </p>
                <div className="space-y-1.5 font-mono text-[11px]" data-id="element-2698">
                  <div className="flex justify-between" data-id="element-2699">
                    <span className="text-muted-foreground" data-id="element-2700">Hash bukti</span>
                    <span data-id="element-2701">sha256: a3f9...c812</span>
                  </div>
                  <div className="flex justify-between" data-id="element-2702">
                    <span className="text-muted-foreground" data-id="element-2703">
                      Berkas terlampir
                    </span>
                    <span data-id="element-2704">3 file (12.4 MB)</span>
                  </div>
                  <div className="flex justify-between" data-id="element-2705">
                    <span className="text-muted-foreground" data-id="element-2706">Petugas</span>
                    <span data-id="element-2707">Tim Komdigi · Aptika</span>
                  </div>
                  <div className="flex justify-between" data-id="element-2708">
                    <span className="text-muted-foreground" data-id="element-2709">Channel</span>
                    <span data-id="element-2710">Government Legal Request</span>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-id="element-2711">
                <p className="font-mono text-[10px] uppercase text-amber-700 font-bold" data-id="element-2712">
                  Timeline
                </p>
                <ul className="text-xs mt-1.5 space-y-1 text-amber-900" data-id="element-2713">
                  <li data-id="element-2714">· Laporan dikirim ({selected.submitted})</li>
                  <li data-id="element-2715">· Acknowledgment platform diterima</li>
                  <li data-id="element-2716">
                    ·{' '}
                    {selected.status === 'REMOVED' ? 'Konten diturunkan' : selected.status === 'ESCALATED' ? 'Dieskalasi ke Trust & Safety senior' : 'Menunggu keputusan platform'}
                  </li>
                </ul>
              </div>
            </div>}
          <DialogFooter data-id="element-2717">
            <Button variant="outline" onClick={() => setSelected(null)} data-id="element-2718">
              Tutup
            </Button>
            <Button onClick={() => {
            toast.info('Membuka halaman platform...');
            setSelected(null);
          }} data-id="element-2719">
              <ExternalLink className="size-3.5 mr-1.5" data-id="element-2720" /> Lihat di Platform
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RelatedWorkflow currentStep="Platform Reporting" nextSteps={[{
      to: '/legal-evidence',
      icon: Gavel,
      label: 'Paket Bukti Forensik',
      desc: 'Lampirkan chain-of-custody ke laporan platform.'
    }, {
      to: '/case-management',
      icon: FileSearch,
      label: 'Berkas Kasus',
      desc: 'Sinkron status takedown ke pipeline hukum.'
    }, {
      to: '/threat-intel',
      icon: ShieldAlert,
      label: 'Update Threat Intel',
      desc: 'Catat aktor pelanggar di basis ancaman.'
    }, {
      to: '/reports',
      icon: FileText,
      label: 'Update Laporan',
      desc: 'Sertakan statistik takedown di laporan resmi.'
    }]} data-id="element-2721" />
    </div>;
}